/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import { FacilityTabItem } from "../components/EditStopPage/Facility/types";
import {
  defaultEquipmentFacilities,
  Equipment,
  SanitaryFacility,
} from "../models/Equipments";
import { getIn } from "../utils";

const EquipmentHelpers = {};

EquipmentHelpers.isTicketMachinePresent = (entity) => {
  return !!getIn(
    entity,
    ["placeEquipments", "ticketingEquipment", "ticketMachines"],
    null,
  );
};

EquipmentHelpers.isTicketOfficePresent = (entity) => {
  return !!getIn(
    entity,
    ["placeEquipments", "ticketingEquipment", "ticketOffice"],
    null,
  );
};

EquipmentHelpers.isTicketCounterPresent = (entity) => {
  return !!getIn(
    entity,
    ["placeEquipments", "ticketingEquipment", "ticketCounter"],
    null,
  );
};

EquipmentHelpers.getTicketingEquipment = (entity) => {
  return getIn(entity, ["placeEquipments", "ticketingEquipment"], null);
};

EquipmentHelpers.isShelterEquipmentPresent = (entity) => {
  return !!getIn(entity, ["placeEquipments", "shelterEquipment"], null);
};

EquipmentHelpers.getSanitaryEquipment = (entity) => {
  return getIn(entity, ["placeEquipments", "sanitaryEquipment"], null);
};

EquipmentHelpers.isWCPresent = (entity) => {
  const sanitaryEquipment = EquipmentHelpers.getSanitaryEquipment(entity);
  return !!(
    sanitaryEquipment &&
    (sanitaryEquipment.numberOfToilets ||
      sanitaryEquipment.sanitaryFacilityList?.includes(
        SanitaryFacility.TOILET,
      ) ||
      EquipmentHelpers.isWCWheelchairAccessible(entity))
  );
};

EquipmentHelpers.getSanitaryFacilityList = (entity) => {
  return getIn(
    entity,
    ["placeEquipments", "sanitaryEquipment", "sanitaryFacilityList"],
    [],
  );
};

EquipmentHelpers.isWCWheelchairAccessible = (entity) => {
  const sanitaryFacilityList = EquipmentHelpers.getSanitaryFacilityList(entity);
  return sanitaryFacilityList.includes(
    SanitaryFacility.WHEEL_CHAIR_ACCESS_TOILET,
  );
};

EquipmentHelpers.is512SignEquipmentPresent = (entity) => {
  const generalSign = getIn(entity, ["placeEquipments", "generalSign"], null);
  if (
    generalSign &&
    generalSign.privateCode &&
    `${generalSign.privateCode.value}` === "512" &&
    generalSign.signContentType === "transportMode"
  ) {
    return true;
  }
  return false;
};

EquipmentHelpers.update512SignEquipment = (entity, payload) => {
  const copyOfEntity = JSON.parse(JSON.stringify(entity));
  return updateEquipmentForEntity(
    copyOfEntity,
    payload,
    Equipment.GENERAL_SIGN,
  );
};

EquipmentHelpers.isWaitingRoomPresent = (entity) => {
  return !!getIn(entity, ["placeEquipments", "waitingRoomEquipment"], null);
};

EquipmentHelpers.isCycleStorageEquipmentPresent = (entity) => {
  return !!getIn(
    entity,
    ["placeEquipments", "cycleStorageEquipment", "numberOfSpaces"],
    null,
  );
};

/**
 * Ticketing equipment contains other items than ticket machines thar are shown in facilities tab separately,
 * the method's purpose to make sure not overwrite those
 */
EquipmentHelpers.getNewTicketingEquipmentStateOnTicketMachinesUpdate = (
  entity,
  value,
) => {
  const newTicketMachinesState = value
    ? defaultEquipmentFacilities[FacilityTabItem.TICKET_MACHINES].isChecked
    : defaultEquipmentFacilities[FacilityTabItem.TICKET_MACHINES].isUnChecked;
  const ticketingEquipment = EquipmentHelpers.getTicketingEquipment(entity);

  return {
    ...ticketingEquipment,
    ...newTicketMachinesState,
  };
};

/**
 * Sanitary equipment isn't just necessarily a WC,
 * the method's purpose to make sure not overwrite the other values of the sanitaryFacilityList
 */
EquipmentHelpers.getNewSanitaryEquipmentStateOnWCUpdate = (entity, value) => {
  const sanitaryEquipment = EquipmentHelpers.getSanitaryEquipment(entity);
  const sanitaryFacilityList = EquipmentHelpers.getSanitaryFacilityList(entity);

  const newWCState = value
    ? defaultEquipmentFacilities[FacilityTabItem.WC].isChecked
    : defaultEquipmentFacilities[FacilityTabItem.WC].isUnChecked;
  const newSanitaryEquipmentState = {
    ...sanitaryEquipment,
    ...newWCState,
  };

  // sanitaryFacilityList to be handled separately here, to not overwrite the values that are not related to WC
  if (value) {
    newSanitaryEquipmentState.sanitaryFacilityList = Array.from(
      new Set([...sanitaryFacilityList, SanitaryFacility.TOILET]),
    );
  } else {
    newSanitaryEquipmentState.sanitaryFacilityList =
      sanitaryFacilityList.filter(
        (f) =>
          f !== SanitaryFacility.TOILET &&
          f !== SanitaryFacility.WHEEL_CHAIR_ACCESS_TOILET,
      );
  }

  return newSanitaryEquipmentState;
};

EquipmentHelpers.updateTicketingEquipmentState = (stopPlace, payload) => {
  let updatedStop = JSON.parse(JSON.stringify(stopPlace));
  return updateEquipmentForEntity(
    updatedStop,
    payload,
    Equipment.TICKETING_EQUIPMENT,
  );
};

EquipmentHelpers.updateShelterEquipmentState = (stopPlace, payload) => {
  let updatedStop = JSON.parse(JSON.stringify(stopPlace));
  return updateEquipmentForEntity(
    updatedStop,
    payload,
    Equipment.SHELTER_EQUIPMENT,
  );
};

EquipmentHelpers.updateSanitaryEquipmentState = (stopPlace, payload) => {
  let updatedStop = JSON.parse(JSON.stringify(stopPlace));
  return updateEquipmentForEntity(
    updatedStop,
    payload,
    Equipment.SANITARY_EQUIPMENT,
  );
};

EquipmentHelpers.updateWaitingRoomState = (stopPlace, payload) => {
  let updatedStop = JSON.parse(JSON.stringify(stopPlace));
  return updateEquipmentForEntity(
    updatedStop,
    payload,
    Equipment.WAITING_ROOM_EQUIPMENT,
  );
};

EquipmentHelpers.updateCycleStorageEquipmentState = (stopPlace, payload) => {
  let updatedStop = JSON.parse(JSON.stringify(stopPlace));
  return updateEquipmentForEntity(
    updatedStop,
    payload,
    Equipment.CYCLE_STORAGE_EQUIPMENT,
  );
};

const updateEquipmentForEntity = (entity, payload, typeOfEquipment) => {
  const { state, type, id } = payload;

  let stateFromCheckbox = typeof state === "boolean";

  let overrideState = null;

  if (stateFromCheckbox) {
    if (state) {
      overrideState = defaultEquipmentFacilities[typeOfEquipment].isChecked;
    } else {
      overrideState = defaultEquipmentFacilities[typeOfEquipment].isUnChecked;
    }
  } else {
    overrideState = state;
  }

  if (type === "stopPlace") {
    if (!entity.placeEquipments) {
      entity.placeEquipments = {};
    }

    entity.placeEquipments[typeOfEquipment] = overrideState;
  } else if (type === "quay") {
    if (entity.quays && entity.quays[id]) {
      if (!entity.quays[id].placeEquipments) {
        entity.quays[id].placeEquipments = {};
      }
      entity.quays[id].placeEquipments[typeOfEquipment] = overrideState;
    }
  }
  return entity;
};

export default EquipmentHelpers;
